import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Scroll, ArrowRight, ArrowDown, Zap, Globe, Clock, Quote, Component, Library, Compass, PenTool, Users, Landmark } from 'lucide-react';
import heroImage from '../assets/clean-hero5.png';
import ancientMap from '../assets/ancient-map-v3.png';
import ancientTimeline from '../assets/ancient_timeline_v7.png';
import culturalBackground from '../assets/cultural-background-v3.png';
import archaeologyImg from '../assets/archaeology-v3.png';
import aiScholar from '../assets/ai_scholar_v3.png';
import ancientContextImg from '../assets/ancient-context-pillar-v3.png';
import biblicalContextImg from '../assets/biblical-context-pillar-v3.png';
import modernContextImg from '../assets/modern-context-pillar-v3.png';
import audienceRelevanceImg from '../assets/audience_relevance_v4.png';
import markManuscriptImg from '../assets/mark_manuscript_v6.png';
import bookPatternsImg from '../assets/book_patterns_v3.png';
import scripturalEchoesImg from '../assets/scriptural_echoes_v3.png';
import expeditionsImg from '../assets/guided_studies_v3.png';
import scholarlyBreadthImg from '../assets/grounded_integrity_v3.png';
import redemptiveTrajectoryImg from '../assets/christ_the_center_v3.png';
import bridgingWorldsImg from '../assets/the_great_cloud_v4.png';
import literaryDesignImg from '../assets/literary_design_v3.png';
import formationImg from '../assets/formation_over_information_v3.png';
import unityOfTruthImg from '../assets/unity_of_truth_v3.png';
import prayerfulDependencyImg from '../assets/prayerful_dependency_v3.png';
import engagementRhythmsImg from '../assets/engagement_rhythms_v3.png';
import { IconTrowel } from '@tabler/icons-react';
import { SEO } from '@/components/seo';
import { motion } from 'framer-motion';

import ancientIcon from '../assets/context-panel-ancient.png';
import biblicalIcon from '../assets/context-panel-biblical.png';
import modernIcon from '../assets/context-panel-modern.png';

const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
};

const staggerContainer = {
    initial: { opacity: 0 },
    whileInView: { opacity: 1, transition: { staggerChildren: 0.2 } },
    viewport: { once: true }
};

export default function HomePage() {
    const [activeSection, setActiveSection] = useState('ancient');
    const [showAllPrinciples, setShowAllPrinciples] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const sections = ['ancient', 'biblical', 'modern'];

            for (const section of sections) {
                const element = document.getElementById(section);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    // If the top of the section is near the top of the viewport
                    if (rect.top <= 120 && rect.bottom >= 120) {
                        setActiveSection(section);
                        break;
                    }
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToSection = (id: string) => {
        const element = document.getElementById(id);
        if (element) {
            const rect = element.getBoundingClientRect();
            const absoluteTop = rect.top + window.pageYOffset;
            window.scrollTo({
                top: absoluteTop - 80,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div className="dark flex min-h-svh flex-col bg-background text-foreground">
            <SEO
                title="BibleGlen - Your Biblical Discovery Expedition"
                description="Explore the Bible with rich historical, cultural, and literary context. An interactive, scholarly guide for your journey through the text."
            />

            {/* Section 1: Hero */}
            <section className="relative h-svh flex items-center justify-center overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-[center_top] md:bg-center z-0 scale-[1.3] md:scale-105"
                    style={{ backgroundImage: `url(${heroImage})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-background z-1" />

                <div className="relative z-10 text-center px-6 max-w-4xl">
                    <motion.h1
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-6xl md:text-8xl font-thin tracking-tighter text-white mb-4"
                    >
                        BibleGlen
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4, duration: 1 }}
                        className="text-xl md:text-3xl font-light text-white/90 mb-8 drop-shadow-lg"
                    >
                        Your expedition into the Biblical world awaits.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.8, duration: 0.5 }}
                        className="flex flex-col items-center gap-6"
                    >
                        <Button
                            size="xl"
                            className="rounded-full px-12 text-lg bg-primary hover:bg-primary/90 transition-all hover:scale-105 shadow-[0_0_20px_rgba(212,175,55,0.3)]"
                            onClick={() => scrollToSection('overview')}
                        >
                            Explore the Path <ArrowDown className="ml-2 h-5 w-5" />
                        </Button>
                        <a href="/app/read" className="text-sm font-medium text-white/90 hover:text-white transition-colors flex items-center gap-1 group">
                            Already exploring? Jump back in <ArrowRight className="h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                        </a>
                    </motion.div>
                </div>

                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 animate-bounce">
                    <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
                        <div className="w-1 h-2 bg-white/50 rounded-full" />
                    </div>
                </div>
            </section>

            <section id="overview" className="py-24 px-6 bg-background relative border-y border-border/50">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div {...fadeInUp}>
                        <h2 className="text-4xl md:text-5xl font-light mb-8 leading-tight">
                            The Bible is an <span className="text-primary italic font-serif">ancient library</span>, but we often read it as a modern blog.
                        </h2>
                        <p className="text-xl text-muted-foreground mb-10 leading-relaxed max-w-2xl mx-auto">
                            While the heart of the text is accessible to everyone, its beauty and depth are profoundly enriched when we understand the world from which it emerged. We equip you to cross that historical and cultural river, allowing the ancient setting to illuminate the enduring message for today.
                        </p>
                        <div className="h-px w-24 bg-primary mx-auto" />
                    </motion.div>
                </div>
            </section>

            {/* Section 3: The Three Contexts (Upgraded Pillars) */}
            <section className="py-24 px-6 bg-muted/30">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-sm uppercase tracking-widest text-primary font-bold mb-4">The Methodology</h2>
                        <h3 className="text-4xl font-light">Layered Discovery</h3>
                    </div>

                    <motion.div
                        variants={staggerContainer}
                        initial="initial"
                        whileInView="whileInView"
                        viewport={{ once: true }}
                        className="grid md:grid-cols-3 gap-12"
                    >
                        {[
                            { id: 'ancient', title: 'Ancient Context', img: ancientContextImg, icon: ancientIcon, desc: 'Explore the world behind the text through interactive geography, chronological timelines, cultural dynamics, and archaeological discovery.' },
                            { id: 'biblical', title: 'Biblical Context', img: biblicalContextImg, icon: biblicalIcon, desc: "Analyze the text's genre, literary structure, and book-wide patterns. Then dig deeper using the interactive manuscript study tools." },
                            { id: 'modern', title: 'Modern Context', img: modernContextImg, icon: modernIcon, desc: 'Bridge the gap to life today through engagement rhythms and guided studies that transform reading into interaction.' }
                        ].map((pillar, index, array) => (
                            <div key={pillar.id} className="relative">
                                <motion.div
                                    variants={fadeInUp}
                                    className="bg-background p-8 rounded-2xl border border-border/50 shadow-sm hover:border-primary/50 transition-all cursor-pointer group flex flex-col h-full"
                                    onClick={() => scrollToSection(pillar.id)}
                                >
                                    <div className="aspect-square mb-6 bg-muted/30 rounded-lg overflow-hidden flex items-center justify-center border border-border/30">
                                        <img src={pillar.img} alt={pillar.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                    </div>
                                    <div className="flex items-center gap-6 mb-6">
                                        <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center overflow-hidden shrink-0">
                                            <img src={pillar.icon} alt={pillar.title} className="w-10 h-10 filter invert object-contain" />
                                        </div>
                                        <h4 className="text-2xl font-semibold text-foreground leading-tight">{pillar.title}</h4>
                                    </div>
                                    <p className="text-muted-foreground leading-relaxed">
                                        {pillar.desc}
                                    </p>
                                </motion.div>

                                {index < array.length - 1 && (
                                    <div className="pointer-events-none">
                                        <div className="absolute top-1/2 -right-10 -translate-y-1/2 z-20 hidden md:block">
                                            <ArrowRight className="w-8 h-8 text-primary/20" />
                                        </div>
                                        <div className="absolute left-1/2 -bottom-8 -translate-x-1/2 z-20 md:hidden">
                                            <ArrowDown className="w-8 h-8 text-primary/20" />
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Step-by-Step Context Journey */}
            <div className="relative">
                {/* Sticky Navigation Bar */}
                <div className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-y border-border/50 py-3 md:py-4">
                    <div className="max-w-6xl mx-auto px-4 md:px-6 flex justify-center gap-4 md:gap-12">
                        {[
                            { id: 'ancient', label: 'Ancient', icon: ancientIcon },
                            { id: 'biblical', label: 'Biblical', icon: biblicalIcon },
                            { id: 'modern', label: 'Modern', icon: modernIcon }
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => scrollToSection(tab.id)}
                                className={`flex items-center gap-2 text-sm font-medium transition-colors ${activeSection === tab.id ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                                    }`}
                            >
                                <img src={tab.icon} alt={tab.label} className={`w-8 h-8 filter invert ${activeSection === tab.id ? '' : 'opacity-60'}`} />
                                {tab.label}
                                {activeSection === tab.id && (
                                    <motion.div layoutId="activeTab" className="absolute -bottom-0 left-0 right-0 h-0.5 bg-primary" />
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                <div id="ancient" className="bg-[#2a2723]/30">
                    <div className="text-center py-16 px-6">
                        <h2 className="text-sm uppercase tracking-widest text-primary font-bold mb-4">Step 01: The Foundation</h2>
                        <h3 className="text-3xl md:text-5xl font-light">The World <span className="text-[#d4af37]">BEHIND</span> the Text</h3>
                    </div>

                    {/* Section 4: Interactive Map */}
                    <section className="py-24 px-6">
                        <div className="max-w-6xl mx-auto">
                            <div className="grid lg:grid-cols-2 gap-16 items-center">
                                <motion.div
                                    initial={{ opacity: 0, x: -30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8 }}
                                >
                                    <h2 className="text-sm uppercase tracking-widest text-primary font-bold mb-4">Geographic Exploration</h2>
                                    <h3 className="text-3xl md:text-4xl lg:text-5xl font-light mb-6">Locate the Story</h3>
                                    <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                                        Stop reading names on a page. Visualize the specific places where sacred history unfolded. We chart every mentioned location - from bustling cities to rugged mountain peaks - on an interactive ancient world map for you to explore.
                                    </p>
                                    <ul className="space-y-4">
                                        <li className="flex items-center gap-3">
                                            <Globe className="text-primary h-5 w-5" />
                                            <span>Specific biblical sites meticulously charted and verified</span>
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <Clock className="text-primary h-5 w-5" />
                                            <span>Historical maps that adapt to reflect ancient borders</span>
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <Zap className="text-primary h-5 w-5" />
                                            <span>Fluid navigation between the text and the terrain</span>
                                        </li>
                                    </ul>
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8 }}
                                    className="relative"
                                >
                                    <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full -z-1" />
                                    <img
                                        src={ancientMap}
                                        alt="Ancient World Map"
                                        className="rounded-2xl border border-border/50 shadow-2xl relative z-10"
                                    />
                                </motion.div>
                            </div>
                        </div>
                    </section>

                    {/* Section 5: Timeline (Placeholder Image) */}
                    <section className="py-24 px-6 bg-muted/30">
                        <div className="max-w-6xl mx-auto">
                            <div className="grid lg:grid-cols-2 gap-16 items-center flex-col-reverse">
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8 }}
                                    className="order-2 lg:order-1"
                                >
                                    <img
                                        src={ancientTimeline}
                                        alt="Ancient World Timeline"
                                        className="rounded-2xl border border-border/50 shadow-2xl"
                                    />
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0, x: 30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8 }}
                                    className="order-1 lg:order-2"
                                >
                                    <h2 className="text-sm uppercase tracking-widest text-primary font-bold mb-4">Chronological Context</h2>
                                    <h3 className="text-3xl md:text-4xl lg:text-5xl font-light mb-6">Trace the Timeline</h3>
                                    <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                                        See the sweep of history as it happened. Every passage is anchored to a synchronized timeline, empowering you to connect the text to the wider world - from the rise of global empires to the words of contemporary prophets.
                                    </p>
                                    <ul className="space-y-4">
                                        <li className="flex items-center gap-3">
                                            <Clock className="text-primary h-5 w-5" />
                                            <span>Scholarly dating for every passage with supporting evidence</span>
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <Globe className="text-primary h-5 w-5" />
                                            <span>Parallel views of contemporary prophets, kings, and global empires</span>
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <Quote className="text-primary h-5 w-5" />
                                            <span>Interactive event cards with deep historical background</span>
                                        </li>
                                    </ul>
                                </motion.div>
                            </div>
                        </div>
                    </section>

                    {/* Section 5.1: Cultural Background (Alternating - Text Left, Image Right) */}
                    <section className="py-24 px-6 bg-background border-b border-border/50">
                        <div className="max-w-6xl mx-auto">
                            <div className="grid lg:grid-cols-2 gap-16 items-center">
                                <motion.div
                                    initial={{ opacity: 0, x: -30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8 }}
                                >
                                    <h2 className="text-sm uppercase tracking-widest text-primary font-bold mb-4">Cultural Dynamics</h2>
                                    <h3 className="text-3xl md:text-4xl lg:text-5xl font-light mb-6">Decode the Culture</h3>
                                    <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                                        Unpack the social and cultural dynamics behind every verse. Instantly access essential context - from honor/shame culture to Ancient Near Eastern parallels - providing the necessary background for you to truly understand the world behind the text.
                                    </p>
                                    <ul className="space-y-4">
                                        <li className="flex items-center gap-3">
                                            <Library className="text-primary h-5 w-5" />
                                            <span>Key cultural background points for every passage</span>
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <Scroll className="text-primary h-5 w-5" />
                                            <span>Direct parallels from ancient texts</span>
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <Users className="text-primary h-5 w-5" />
                                            <span>Detailed profiles on ancient people, groups, and social structures</span>
                                        </li>
                                    </ul>
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8 }}
                                    className="relative"
                                >
                                    <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full -z-1" />
                                    <img
                                        src={culturalBackground}
                                        alt="Cultural Background Illustration"
                                        className="rounded-2xl border border-border/50 shadow-2xl relative z-10"
                                    />
                                </motion.div>
                            </div>
                        </div>
                    </section>

                    {/* Section 5.2: Archaeology (Alternating - Image Left, Text Right) */}
                    <section className="py-24 px-6 bg-muted/30 border-b border-border/50">
                        <div className="max-w-6xl mx-auto">
                            <div className="grid lg:grid-cols-2 gap-16 items-center flex-col-reverse">
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8 }}
                                    className="order-2 lg:order-1"
                                >
                                    <img
                                        src={archaeologyImg}
                                        alt="Archaeology Illustration"
                                        className="rounded-2xl border border-border/50 shadow-2xl"
                                    />
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0, x: 30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8 }}
                                    className="order-1 lg:order-2"
                                >
                                    <h2 className="text-sm uppercase tracking-widest text-primary font-bold mb-4">Archaeological Discovery</h2>
                                    <h3 className="text-3xl md:text-4xl lg:text-5xl font-light mb-6">Unearth the Evidence</h3>
                                    <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                                        See the physical evidence that grounds the biblical narrative in history. From the gates of Hazor to the scrolls of Qumran, explore the artifacts and excavation reports that verify the biblical setting.
                                    </p>
                                    <ul className="space-y-4">
                                        <li className="flex items-center gap-3">
                                            <Landmark className="text-primary h-5 w-5" />
                                            <span>Direct links between archaeological finds and specific verses</span>
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <IconTrowel className="text-primary h-5 w-5" />
                                            <span>Instant visual access to ancient artifacts and dig sites</span>
                                        </li>
                                    </ul>
                                </motion.div>
                            </div>
                        </div>
                    </section>

                </div>

                {/* ZONE 2: BIBLICAL CONTEXT */}
                <div id="biblical" className="bg-background">
                    <div className="text-center py-16 px-6 border-t border-border/50">
                        <h2 className="text-sm uppercase tracking-widest text-primary font-bold mb-4">Step 02: The Discovery</h2>
                        <h3 className="text-3xl md:text-5xl font-light">The World <span className="text-[#d4af37]">WITHIN</span> the Text</h3>
                    </div>

                    <section className="py-24 px-6">
                        <div className="max-w-6xl mx-auto">
                            <div className="grid md:grid-cols-2 gap-12">
                                {[
                                    {
                                        icon: Scroll,
                                        image: literaryDesignImg,
                                        title: "Discover Literary Design",
                                        description: "To understand the logic of the message, we must first see the architecture of the text. From poetic chiasms to narrative framing, gain the eyes to recognize the intentional design and tools used to shape the message."
                                    },
                                    {
                                        icon: PenTool,
                                        image: markManuscriptImg,
                                        title: "Mark the Manuscript",
                                        description: "Move from passive reading to active investigation. Use interactive tools to circle key verbs, highlight repeated motifs, and trace connections directly on the page."
                                    },
                                    {
                                        icon: Component,
                                        image: bookPatternsImg,
                                        title: "Analyze Book Patterns",
                                        description: "See the forest and the trees. Understand where each passage fits within its book, and identify the recurring patterns, themes, and structures that span the whole document."
                                    },
                                    {
                                        icon: Library,
                                        image: scripturalEchoesImg,
                                        title: "Trace Scriptural Echoes",
                                        description: "See how the entire library speaks as one. Trace the intertextual web of echoes, allusions, and prophetic promises that reveal the consistency and depth of the biblical canon."
                                    }
                                ].map((feature, index) => (
                                    <motion.div
                                        key={index}
                                        variants={fadeInUp}
                                        className="group p-8 bg-muted/20 border border-border/50 rounded-2xl hover:bg-muted/30 transition-all flex flex-col items-center text-center"
                                    >
                                        <div className="aspect-square w-full mb-8 bg-muted/40 rounded-xl overflow-hidden flex items-center justify-center border border-border/30 group-hover:bg-muted/50 transition-colors">
                                            <img src={feature.image} alt={feature.title} className="w-full h-full object-cover transition-transform group-hover:scale-110" />
                                        </div>
                                        <h4 className="text-2xl font-semibold mb-4">{feature.title}</h4>
                                        <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </section>
                </div>

                {/* ZONE 3: MODERN CONTEXT */}
                <div id="modern" className="bg-slate-900/40">
                    <div className="text-center py-16 px-6 border-t border-border/50">
                        <h2 className="text-sm uppercase tracking-widest text-primary font-bold mb-4">Step 03: The Integration</h2>
                        <h3 className="text-3xl md:text-5xl font-light">The World <span className="text-[#d4af37]">WITH</span> the Text</h3>
                    </div>

                    {/* Engagement Rhythms */}
                    <section className="py-24 px-6">
                        <div className="max-w-6xl mx-auto">
                            <div className="grid lg:grid-cols-2 gap-16 items-start">
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8 }}
                                    className="order-2 lg:order-1 relative"
                                >
                                    <div className="aspect-[3/4] rounded-2xl border border-border/50 overflow-hidden relative shadow-2xl">
                                        <div className="absolute inset-0 bg-primary/10 blur-3xl -z-1" />
                                        <img src={engagementRhythmsImg} alt="Engagement Rhythms" className="w-full h-full object-cover" />
                                    </div>
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8 }}
                                    className="order-1 lg:order-2"
                                >
                                    <div className="mb-12">
                                        <h2 className="text-sm uppercase tracking-widest text-primary font-bold mb-4">Beyond the Study</h2>
                                        <h3 className="text-4xl font-light mb-6">Engagement Rhythms</h3>
                                        <p className="text-lg text-muted-foreground leading-relaxed">
                                            Scripture was never meant to be merely analyzed - it was meant to be experienced. We provide the tools to practice time-tested rhythms of reflection, bringing ancient traditions into your modern study.
                                        </p>
                                    </div>

                                    <div className="space-y-4 mb-8">
                                        {[
                                            { title: "Lectio Divina", type: "Contemplative", desc: "A prayerful reading that listens for the 'living voice' of the Spirit." },
                                            { title: "Scriptural Rumination", type: "Meditative", desc: "The ancient practice of 'chewing' on a phrase until it shapes your soul." },
                                            { title: "Dramatic Storying", type: "Narrative", desc: "Reliving the word as an oral proclamation to be heard and felt together." },
                                            { title: "Prayer of the Senses", type: "Immersive", desc: "Entering the story through holy imagination to hear the 'still small voice.'" },
                                            // { title: "Shared Discernment", type: "Relational", desc: "Wrestling with the text's challenges alongside a faithful community." }
                                        ].map((item, index) => (
                                            <div key={index} className="p-5 rounded-2xl border border-border/40 bg-background/50 hover:border-primary/30 transition-colors group">
                                                <div className="flex items-start gap-4">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-primary/40 mt-2.5 group-hover:scale-150 transition-transform" />
                                                    <div>
                                                        <span className="text-[9px] uppercase tracking-widest text-primary/70 font-bold block mb-0.5">{item.type}</span>
                                                        <h4 className="text-lg font-semibold mb-1">{item.title}</h4>
                                                        <p className="text-sm text-muted-foreground leading-relaxed italic">{item.desc}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <p className="text-sm text-muted-foreground italic pl-6">
                                        ...and dozens of other ancient and modern rhythms waiting to be explored.
                                    </p>
                                </motion.div>
                            </div>
                        </div>
                    </section>

                    {/* Guided Studies Section */}
                    <section className="py-24 px-6 bg-background/40 border-t border-border/30">
                        <div className="max-w-6xl mx-auto">
                            <div className="grid lg:grid-cols-2 gap-16 items-center">
                                <motion.div
                                    initial={{ opacity: 0, x: -30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8 }}
                                >
                                    <h2 className="text-sm uppercase tracking-widest text-primary font-bold mb-4">Personalized Discovery</h2>
                                    <h3 className="text-4xl lg:text-5xl font-light mb-6">Guided Studies</h3>
                                    <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                                        A framework for your journey. Iterate through the whole process: reading the text, making observations, exploring ancient context, and synthesizing insights into faithful application - all tailored to your personal background and persona.
                                    </p>
                                    <ul className="space-y-4">
                                        <li className="flex items-center gap-3">
                                            <Users className="text-primary h-5 w-5" />
                                            <span>Studies tailored to your specific persona and experience level</span>
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <Compass className="text-primary h-5 w-5" />
                                            <span>Step-by-step navigation from first reading to life change</span>
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <PenTool className="text-primary h-5 w-5" />
                                            <span>Integrated tools for marking, mapping, and historical research</span>
                                        </li>
                                    </ul>
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8 }}
                                    className="relative"
                                >
                                    <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full -z-1" />
                                    <img
                                        src={expeditionsImg}
                                        alt="Guided Studies Illustration"
                                        className="rounded-2xl border border-border/50 shadow-2xl relative z-10"
                                    />
                                </motion.div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            {/* FOUNDATIONAL METHODOLOGY ZONE */}
            <div id="methodology-zone" className="relative bg-[#0a0a0b]">
                {/* Visual Divider / Transition Heading */}
                <div className="pt-24 bg-gradient-to-b from-slate-900/50 to-[#0a0a0b] border-t-2 border-primary/20">
                    <div className="max-w-4xl mx-auto text-center">
                        <motion.div variants={fadeInUp}>
                            <div className="w-16 h-1 bg-primary/40 mx-auto mb-10 rounded-full shadow-[0_0_15px_rgba(212,175,55,0.3)]" />
                            <h2 className="text-4xl md:text-5xl font-light tracking-tight text-white mb-4 italic font-serif">A Path Guided by Principle</h2>
                            <p className="text-lg text-muted-foreground/70 max-w-2xl mx-auto px-6 leading-relaxed">
                                Discoveries are anchored in a rigorous scholarly framework and deep theological convictions. This is the compass that guides every expedition.
                            </p>
                        </motion.div>
                    </div>
                </div>

                {/* THE COMPASS: THEOLOGICAL PRINCIPLES */}
                <div id="principles" className="bg-[#0a0a0b]">
                    <section className="py-24 px-6 border-y border-border/10">
                        <div className="max-w-6xl mx-auto">
                            {/* <div className="text-center mb-16">
                                <h2 className="text-sm uppercase tracking-widest text-[#d4af37] font-bold mb-4">The Compass</h2>
                                <p className="text-muted-foreground max-w-xl mx-auto italic font-serif opacity-70">
                                    Guided by convictions that honor the text and empower the student.
                                </p>
                            </div> */}
                            <div className={`${showAllPrinciples ? 'grid md:grid-cols-2 gap-x-12 gap-y-16' : 'hidden'} max-w-5xl mx-auto`}>
                                {[
                                    {
                                        title: "Audience Relevance",
                                        image: audienceRelevanceImg,
                                        description: "The Bible was written FOR us, but not TO us. Recovering the original context isn't a secret formula, but a lens that brings the author's intent into focus - deepening our understanding and grounding our application."
                                    },
                                    {
                                        title: "Christ the Center",
                                        image: redemptiveTrajectoryImg,
                                        description: "Jesus is the center and climax of the biblical narrative. Every passage participates in the unfolding story of God's redemptive work, finding its ultimate fulfillment in Him."
                                    },
                                    {
                                        title: "Formation over Information",
                                        image: formationImg,
                                        description: "The end of all biblical study is the love of God and neighbor. Technical mastery of the ancient world serves a higher purpose: to encounter the living God and be transformed into the image of Christ."
                                    },
                                    {
                                        title: "The Great Cloud",
                                        image: bridgingWorldsImg,
                                        description: "Scripture is a gift to the whole people of God. Engaging with the global and historical church protects us from individual bias and exposes our own cultural blind spots."
                                    },
                                    {
                                        title: "Literary Design",
                                        image: literaryDesignImg,
                                        description: "Scripture is a masterpiece of intentional design by both the human and divine authors. God chose to speak through poetry, narrative, and metaphor, and we honor the text by respecting the unique artistry and 'rules' of its various genres."
                                    },
                                    {
                                        title: "Grounded Integrity",
                                        image: scholarlyBreadthImg,
                                        description: "Rigorous historical and linguistic research serves as a steward of the truth, ensuring our interpretations are anchored in the objective reality of the biblical world rather than modern assumptions."
                                    },
                                    {
                                        title: "The Unity of Truth",
                                        image: unityOfTruthImg,
                                        description: "All truth is God’s truth. We do not fear the discoveries of archaeology, history, or science, but welcome them as partners that help us better understand the reality the Bible describes."
                                    },
                                    {
                                        title: "Prayerful Dependency",
                                        image: prayerfulDependencyImg,
                                        description: "While scholarship provides the data, the Holy Spirit provides the illumination. We pair rigorous study with prayerful humility, trusting the Spirit to speak truthfully to the heart of every seeker."
                                    }
                                ].map((item, index) => (
                                    <motion.div key={index} variants={fadeInUp} className="group flex flex-col">
                                        <div className="aspect-[4/3] md:aspect-square mb-6 rounded-2xl border border-border/50 overflow-hidden relative shadow-sm max-w-md mx-auto w-full">
                                            <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/70 to-transparent p-8 pt-20">
                                                <h4 className="font-bold text-white text-3xl tracking-tight leading-tight">{item.title}</h4>
                                            </div>
                                        </div>
                                        <p className="text-sm text-muted-foreground leading-relaxed px-2 text-center md:text-left">{item.description}</p>
                                    </motion.div>
                                ))}
                            </div>

                            <div className={`${showAllPrinciples ? 'mt-20' : 'mt-0'} flex flex-col items-center gap-6`}>
                                {!showAllPrinciples && (
                                    <Button
                                        onClick={() => setShowAllPrinciples(true)}
                                        variant="outline"
                                        className="rounded-full border-primary/30 hover:border-primary/60 px-10 py-6 text-lg transition-all"
                                    >
                                        Expand the Compass
                                    </Button>
                                )}
                                <Button asChild variant="ghost" className="text-muted-foreground hover:text-foreground transition-all">
                                    <a href="/app/field-guide/our-core-convictions">
                                        Read our full Scholarly Methodology <ArrowRight className="ml-2 h-4 w-4" />
                                    </a>
                                </Button>
                            </div>
                        </div>
                    </section>
                </div>

                {/* AI Scholar Section */}
                <section className="py-24 px-6 bg-[#020617] border-t border-border/20">
                    <div className="max-w-6xl mx-auto">
                        <div className="grid lg:grid-cols-2 gap-16 items-center">
                            <motion.div variants={fadeInUp}>
                                <h2 className="text-sm uppercase tracking-widest text-primary font-bold mb-4">The AI Scholar</h2>
                                <h3 className="text-4xl lg:text-5xl font-light mb-6">A Research Assistant for Your Journey</h3>
                                <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                                    BibleGlen’s AI serves as a high-fidelity curator, meticulously summarizing vast libraries of peer-reviewed research to surface the scholarly consensus. By pre-processing complex data across archaeology, history, and literature, we provide the grounded evidence you need to make your own informed discoveries.
                                </p>
                                <div className="flex flex-wrap gap-4">
                                    <Button asChild variant="outline" className="rounded-full border-primary/30 hover:border-primary/60 transition-colors">
                                        <a href="/app/field-guide/how-we-use-ai">
                                            Our Scholarly AI Methodology <ArrowRight className="ml-2 h-4 w-4" />
                                        </a>
                                    </Button>
                                </div>
                            </motion.div>
                            <motion.div variants={fadeInUp} className="relative">
                                <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full -z-1" />
                                <img
                                    src={aiScholar}
                                    alt="AI Scholar Perspective"
                                    className="rounded-2xl border border-border/50 shadow-2xl relative z-10"
                                />
                            </motion.div>
                        </div>
                    </div>
                </section>
            </div>

            {/* Section 7: Final CTA */}
            <section className="py-32 px-6 bg-primary text-primary-foreground relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white/10 to-transparent opacity-50" />
                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <motion.div {...fadeInUp}>
                        <h2 className="text-4xl md:text-6xl font-light mb-8">The story is waiting.</h2>
                        <p className="text-xl opacity-90 mb-12 max-w-2xl mx-auto leading-relaxed">
                            Equip yourself with the tools of discovery and step into the world behind, within, and before the text. Your expedition into the heart of Scripture begins here.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Button asChild size="xl" variant="secondary" className="rounded-full px-12 text-lg hover:scale-105 transition-transform">
                                <a href="/app/read">Begin the Expedition</a>
                            </Button>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-16 px-6 border-t border-border/50 text-center">
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-xl font-light tracking-widest text-foreground/80 mb-6 uppercase">BibleGlen</h2>
                    <div className="flex justify-center flex-wrap gap-x-8 gap-y-4 mb-8 text-sm text-muted-foreground">
                        <a href="/app/read" className="hover:text-primary transition-colors">Launch App</a>
                    </div>
                    <p className="text-[10px] text-muted-foreground/50 italic opacity-80 uppercase tracking-widest">
                        © {new Date().getFullYear()} BibleGlen. All rights reserved.
                    </p>
                </div>
            </footer>
        </div >
    );
}
